import callGraphQLApi, {
  GQLApiDispatch,
  GQLApiGetState,
} from '@helpers/callGraphQLApi';
import { getLatestSchemaId } from '@common/state/modules/notification/services';
import * as Actions from '../actions';
import {
  createSettingsProfileQuery,
  deleteSettingsProfileQuery,
  fetchProfileListQuery,
  updateSettingsProfileQuery,
} from './queries';
import { modules } from '@veritone/glc-redux';
import {
  CreateSettingsProfileResponse,
  DeleteSettingsProfileResponse,
  FetchProfileListResponse,
  SettingsProfileType,
  UpdateSettingsProfileResponse,
} from '../models';
import { Head } from 'ts-essentials';
const {
  config: { getConfig },
} = modules;

const NOOP = 'noop';
export const invokeGqlCall =
  <T>(
    query: string,
    variables: object,
    actionTypes: Head<Parameters<typeof callGraphQLApi<T>>>['actionTypes']
  ) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) =>
    await callGraphQLApi<T>({
      actionTypes,
      query,
      variables,
      dispatch,
      getState,
    });

export const lookupLatestSettingsProfileSchemaId = (
  () => async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const { settingsProfileRegistryId: dataRegistryId, apiRoot } =
      getConfig<Window['config']>(getState());
    return await getLatestSchemaId(
      dispatch,
      getState,
      dataRegistryId,
      'settingsProfileRegistryId',
      apiRoot
    );
  }
)();

export const fetchProfileList =
  (limit: number, offset: number) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestSettingsProfileSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<FetchProfileListResponse>({
      actionTypes: [
        NOOP,
        Actions.FETCH_PROFILE_LIST_SUCCESS,
        Actions.FETCH_PROFILE_LIST_FAILURE,
      ],
      query: fetchProfileListQuery,
      variables: { schemaId, limit, offset },
      dispatch,
      getState,
    });
  };

export const deleteSettingsProfile =
  (profileId: string) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestSettingsProfileSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<DeleteSettingsProfileResponse>({
      actionTypes: [
        NOOP,
        Actions.DELETE_SETTINGS_PROFILE_SUCCESS,
        Actions.DELETE_SETTINGS_PROFILE_FAILURE,
      ],
      query: deleteSettingsProfileQuery,
      variables: { profileId, schemaId },
      dispatch,
      getState,
    });
  };

export const updateSettingsProfile =
  (profile: SettingsProfileType) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestSettingsProfileSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<UpdateSettingsProfileResponse>({
      actionTypes: [
        NOOP,
        Actions.UPDATE_SETTINGS_PROFILE_SUCCESS,
        Actions.UPDATE_SETTINGS_PROFILE_FAILURE,
      ],
      query: updateSettingsProfileQuery,
      variables: { profileId: profile.id, data: profile.data, schemaId },
      dispatch,
      getState,
    });
  };

/**
 * Write a profile back with `isDeleted` flipped.
 *
 * The same upsert `updateSettingsProfile` makes, reporting through its own
 * actions so a delete is not announced — or failed — as an "update".
 */
export const setSettingsProfileDeleted =
  (profile: SettingsProfileType) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestSettingsProfileSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<UpdateSettingsProfileResponse>({
      actionTypes: [
        NOOP,
        Actions.SET_SETTINGS_PROFILE_DELETED_SUCCESS,
        Actions.SET_SETTINGS_PROFILE_DELETED_FAILURE,
      ],
      query: updateSettingsProfileQuery,
      variables: { profileId: profile.id, data: profile.data, schemaId },
      dispatch,
      getState,
    });
  };

export const createSettingsProfile =
  (data: SettingsProfileType['data']) =>
  async (dispatch: GQLApiDispatch, getState: GQLApiGetState) => {
    const schemaId = await lookupLatestSettingsProfileSchemaId(
      dispatch,
      getState
    );
    if (!schemaId) {
      return;
    }
    return await callGraphQLApi<CreateSettingsProfileResponse>({
      actionTypes: [
        NOOP,
        Actions.CREATE_SETTINGS_PROFILE_SUCCESS,
        Actions.CREATE_SETTINGS_PROFILE_FAILURE,
      ],
      query: createSettingsProfileQuery,
      variables: { data, schemaId },
      dispatch,
      getState,
    });
  };
